import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert,
} from '@mui/material';
import { accountingApi } from '../../services/api';
import { TrialBalanceRow } from '../../types';

const TrialBalance: React.FC = () => {
  const [rows, setRows] = useState<TrialBalanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await accountingApi.getTrialBalance();
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err: any) {
      setError('Failed to load trial balance.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalDebits = rows.reduce((s, r) => s + r.debitBalance, 0);
  const totalCredits = rows.reduce((s, r) => s + r.creditBalance, 0);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Trial Balance</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Account Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Account Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Debit</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No journal entries posted yet. Generate invoices or record payments to see balances.</Typography></TableCell></TableRow>
              ) : (
                rows.map((r, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.accountCode}</TableCell>
                    <TableCell>{r.accountName}</TableCell>
                    <TableCell>{r.type}</TableCell>
                    <TableCell align="right">{r.debitBalance > 0 ? formatCurrency(r.debitBalance) : '-'}</TableCell>
                    <TableCell align="right">{r.creditBalance > 0 ? formatCurrency(r.creditBalance) : '-'}</TableCell>
                  </TableRow>
                ))
              )}
              {rows.length > 0 && (
                <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 700 } }}>
                  <TableCell colSpan={3}>Totals</TableCell>
                  <TableCell align="right">{formatCurrency(totalDebits)}</TableCell>
                  <TableCell align="right">{formatCurrency(totalCredits)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TrialBalance;
